import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close,
  Print,
  Download,
  Preview,
} from '@mui/icons-material';
import api from '../services/api';

const LabelPrintDialog = ({ open, onClose, items = [] }) => {
  const [labelSize, setLabelSize] = useState('medium');
  const [includeQR, setIncludeQR] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const handleSizeChange = (event) => {
    setLabelSize(event.target.value);
    setPreviewUrl(null);
  };

  const handleQRChange = (event) => {
    setIncludeQR(event.target.checked);
    setPreviewUrl(null);
  };

  const generatePreview = async () => {
    if (items.length === 0) return;

    setLoading(true);
    setError(null);
    try {
      if (items.length === 1) {
        // Single label
        const params = new URLSearchParams({
          size: labelSize,
          include_qr: includeQR.toString(),
        });
        const response = await api.get(
          `/inventory/items/${items[0].id}/label?${params.toString()}`,
          { responseType: 'blob' }
        );
        const url = URL.createObjectURL(response.data);
        setPreviewUrl(url);
      } else {
        // Batch labels
        const response = await api.post(
          '/inventory/labels/batch',
          {
            item_ids: items.map((item) => item.id),
            size: labelSize,
            include_qr: includeQR,
          },
          { responseType: 'blob' }
        );
        const url = URL.createObjectURL(response.data);
        setPreviewUrl(url);
      }
    } catch (err) {
      setError('Failed to generate label preview');
      console.error('Label generation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    if (!previewUrl) {
      await generatePreview();
      return;
    }

    // Open PDF in new window for printing
    const printWindow = window.open(previewUrl, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  const handleDownload = async () => {
    if (!previewUrl) {
      await generatePreview();
      return;
    }

    const link = document.createElement('a');
    link.href = previewUrl;
    link.download = `labels_${items.length}_items_${Date.now()}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClose = () => {
    setPreviewUrl(null);
    setError(null);
    onClose();
  };

  const getLabelInfo = () => {
    switch (labelSize) {
      case 'small':
        return '2.5" x 1.5" (20 per page)';
      case 'large':
        return '5" x 3" (4 per page)';
      default:
        return '4" x 2" (10 per page)';
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Print Labels {items.length > 0 && `(${items.length} item${items.length > 1 ? 's' : ''})`}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {items.length === 0 ? (
          <Alert severity="info">
            No items selected. Please select at least one item to print labels.
          </Alert>
        ) : (
          <Box>
            <Box mb={3}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Label Size</FormLabel>
                <RadioGroup value={labelSize} onChange={handleSizeChange}>
                  <FormControlLabel
                    value="small"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2">Small</Typography>
                        <Typography variant="caption" color="text.secondary">
                          2.5" x 1.5" - 20 labels per page
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="medium"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2">Medium (Recommended)</Typography>
                        <Typography variant="caption" color="text.secondary">
                          4" x 2" - 10 labels per page
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="large"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2">Large</Typography>
                        <Typography variant="caption" color="text.secondary">
                          5" x 3" - 4 labels per page
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            <Box mb={3}>
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox checked={includeQR} onChange={handleQRChange} />}
                  label={
                    <Box>
                      <Typography variant="body2">Include QR Code</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Use QR code instead of barcode for easier mobile scanning
                      </Typography>
                    </Box>
                  }
                />
              </FormGroup>
            </Box>

            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2" gutterBottom>
                Label Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Each label will include:
              </Typography>
              <ul style={{ margin: '8px 0', paddingLeft: '24px' }}>
                <li>
                  <Typography variant="body2">Item name</Typography>
                </li>
                <li>
                  <Typography variant="body2">Price</Typography>
                </li>
                <li>
                  <Typography variant="body2">SKU</Typography>
                </li>
                <li>
                  <Typography variant="body2">Condition (if available)</Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    {includeQR ? 'QR Code' : 'Barcode'} for scanning
                  </Typography>
                </li>
              </ul>
            </Paper>

            {previewUrl && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Preview
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    height: 400,
                    overflow: 'auto',
                    bgcolor: 'grey.100',
                  }}
                >
                  <iframe
                    src={previewUrl}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                    }}
                    title="Label Preview"
                  />
                </Paper>
              </Box>
            )}

            {loading && (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        {items.length > 0 && (
          <>
            <Button
              startIcon={<Preview />}
              onClick={generatePreview}
              disabled={loading}
            >
              Preview
            </Button>
            <Button
              startIcon={<Download />}
              onClick={handleDownload}
              disabled={loading}
            >
              Download PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<Print />}
              onClick={handlePrint}
              disabled={loading}
            >
              Print
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LabelPrintDialog;
